import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import EntityForm from "../../components/ui/EntityForm";
import { toast } from "react-toastify";
import { LoaderPinwheel } from "lucide-react";
import { hasPermission } from "../../utils/permissions";
import PageSizeSelect from "../../components/ui/PageSizeSelect";

const ProveedoresPage = () => {
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filteredProveedores, setFilteredProveedores] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [selectedProveedor, setSelectedProveedor] = useState(null);

  // 🔹 Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/prov";

  const fetchProveedores = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProveedores(res.data);
      setFilteredProveedores(res.data);
    } catch (err) {
      toast.error("Error al obtener proveedores ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProveedores();
    // eslint-disable-next-line
  }, []);

  // 🔍 Filtro de búsqueda
  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();
    const filtered = proveedores.filter(
      (p) =>
        p.name.toLowerCase().includes(lowerSearch) ||
        p.nit.toLowerCase().includes(lowerSearch) ||
        p.email.toLowerCase().includes(lowerSearch),
    );
    setFilteredProveedores(filtered);
    setCurrentPage(1);
  }, [searchTerm, proveedores]);

  // 🔹 Paginación lógica
  const totalPages = Math.ceil(filteredProveedores.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentProveedores = filteredProveedores.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleEdit = (proveedor) => {
    setSelectedProveedor(proveedor);
    setShowModal(true);
  };

  const handleCreate = () => {
    setSelectedProveedor(null);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    try {
      if (selectedProveedor) {
        await axios.put(`${API_URL}/${selectedProveedor._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Proveedor actualizado correctamente ✅");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Proveedor creado correctamente ✅");
      }

      setShowModal(false);
      fetchProveedores();
    } catch (err) {
      const backendMessage =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Ocurrió un error inesperado al guardar el proveedor";
      toast.error(backendMessage);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este proveedor?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.info("Proveedor eliminado correctamente 🗑️");
      fetchProveedores();
    } catch (err) {
      toast.error("Error al eliminar proveedor");
    }
  };

  return (
    <Card className="shadow-md">
      <CardContent>
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold">Proveedores</h2>
          <div className="flex gap-2 items-center w-full sm:w-auto">
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {hasPermission("proveedores.crear") && (
              <Button onClick={handleCreate}>+ Nuevo Proveedor</Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
            Cargando datos, por favor espera...
          </div>
        )}

        <div className="flex items-center justify-end mb-2">
          <PageSizeSelect
            value={itemsPerPage}
            onChange={(size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            }}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-sm">
            <thead className="bg-gray-100 text-gray-700 text-sm uppercase">
              <tr>
                <th className="py-2 px-4 text-left">Nombre</th>
                <th className="py-2 px-4 text-left">Nit</th>
                <th className="py-2 px-4 text-left">Rol</th>
                <th className="py-2 px-4 text-left">Correo</th>
                <th className="py-2 px-4 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {currentProveedores.map((p) => (
                <tr key={p._id} className="border-b hover:bg-gray-50">
                  <td className="py-2 px-4">{p.name}</td>
                  <td className="py-2 px-4">{p.nit}</td>
                  <td className="py-2 px-4">{p.role}</td>
                  <td className="py-2 px-4">{p.email}</td>
                  <td className="py-2 px-4 text-right">
                    {hasPermission("proveedores.editar") && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(p)}
                      >
                        Editar
                      </Button>
                    )}
                    {hasPermission("proveedores.eliminar") && (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="ml-2 bg-red-500 hover:bg-red-600 text-white"
                        onClick={() => handleDelete(p._id)}
                      >
                        Eliminar
                      </Button>
                    )}
                  </td>
                </tr>
              ))}

              {currentProveedores.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-4 text-gray-500 italic"
                  >
                    No se encontraron proveedores.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 🔹 Paginación */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm text-gray-600 gap-3">
            <span>
              Mostrando {currentProveedores.length} de{" "}
              {filteredProveedores.length} resultados
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                ← Anterior
              </Button>
              <span className="px-2 py-1 bg-gray-100 rounded-md">
                Página {currentPage} de {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}

        {showModal && (
          <EntityForm
            type="proveedor"
            entity={selectedProveedor}
            onClose={() => setShowModal(false)}
            onSave={handleSave}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ProveedoresPage;
